import { faLocationDot, faTrophy } from '@fortawesome/free-solid-svg-icons';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { get, sortBy } from 'lodash';
import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import ContentContainer from '~/src/components/ContentContainer';
import Games from '~/src/components/Index/Games';
import Layout from '~/src/components/Layout';
import NotFound from '~/src/components/NotFound';
import Suspended from '~/src/components/Suspended';
import TBD from '~/src/components/TBD';
import Table from '~/src/components/Table';
import TeamsLinks from '~/src/components/Teams/Links';
import Logo from '~/src/components/Teams/Logo';
import styles from '~/src/lib/styles.json';
import { Ot7Player } from '~/src/models/Ot7Player';
import { Ot7Team } from '~/src/models/Ot7Team';
import { useSetDocumentState } from '~/src/overtime-lib/src/www/contexts/Document';
import useFetch from '~/src/overtime-lib/src/www/hooks/useFetch';
import { Urls } from '../../lib/enums/urls';
import { Ot7Division } from '../../models/Ot7Division';
import { Ot7Game } from '../../models/Ot7Game';
import { Ot7Season } from '../../models/Ot7Season';
import FeaturedPlayers from '../FeaturedPlayers';
import './Team.scss';

const _Team = () => {
	const { id } = useParams();
	const { hash } = useLocation();

	const fetchTeams = useFetch<Ot7Team>({
		url: `${Urls.RestApiBaseUrl}/ot7_teams/v1/public/${id}`,
		key: 'ot7_team',
	});
	const team = fetchTeams.result.read();
	if (!team) {
		return <NotFound />;
	}

	useSetDocumentState({ title: team.name });

	const fetchSeason = useFetch<Ot7Season>({
		url: `${Urls.RestApiBaseUrl}/ot7_seasons/v1/public/current`,
		key: 'ot7_season',
	});
	const season = fetchSeason.result.read();

	const [currentHeader, setCurrentHeader] = useState<string>(hash.slice(1));
	const playersFetchResult = useFetch<Ot7Player[]>({
		url: `${Urls.RestApiBaseUrl}/ot7_teams/v1/public/${id}/ot7_players`,
		key: 'ot7_players',
	});
	const players = playersFetchResult.result.read().map((p) => ({ ...p, ot7_team: team }));

	const playersWithStats = players.map((p) => ({
		...p,
		season: p.ot7_seasons_ot7_players.find((s) => s.ot7_season.is_current),
	}));

	const sortedPlayers = sortBy(playersWithStats, [
		currentHeader ? (p) => (currentHeader === 'position' ? p.position : -1 * (get(p, currentHeader, 0) || 0)) : null,
		'family_name',
		'first_name',
	]);

	const playerHeaders = [
		['position', 'P'],
		['height', 'Ht'],
		['weight', 'Wt'],
		// ['season.touchdowns', 'TD'],
		// ['season.pass_attempts', 'Att'],
		// ['season.pass_completions', 'Comp'],
		// ['season.receptions', 'Rec'],
		// ['season.interceptions', 'Int'],
		['hometown', 'City'],
	];

	const fetchGames = useFetch<Ot7Game[]>({
		url: `${Urls.RestApiBaseUrl}/ot7_games/v1/public?all=true&ot7_team_id=${team.id}&ot7_season_id=${season.id}`,
		key: 'ot7_games',
	});
	const games = fetchGames.result.read();

	// Hardcoded event ID for 2024 championship weekend
	const championshipGames = games.filter((g) => g.ot7_event_id == 'ee42727d-87a0-46f4-84e4-6f3f74e94be3');
	const teamsInChampionship = championshipGames.map((g) => g.ot7_games_ot7_teams);
	const teamIdsInChampionship = teamsInChampionship.flat().map((t) => t.ot7_team_id);
	const isInvitedToChampionship = teamIdsInChampionship.includes(team.id);

	// Determine if team has won a division
	const divisionsResult = useFetch<Ot7Division[]>({
		url: `${Urls.RestApiBaseUrl}/ot7_divisions/v1/public`,
		key: 'ot7_divisions',
	});

	const divisions = divisionsResult.result.read();
	const division_won = divisions?.find((d) => d.winner_ot7_team_id == team.id);

	const division = divisions?.find((division) => team.ot7_division_id === division.id);

	// Calculate wins, losses, and draws from games
	const record = { wins: 0, losses: 0, draws: 0 };
	for (var game of games.filter((g) => g.status === 'confirmed')) {
		var gameTeam = game.ot7_games_ot7_teams.find((t) => t.ot7_team_id === team.id);
		var gameOpponent = game.ot7_games_ot7_teams.find((t) => t.ot7_team_id !== team.id);

		if (gameTeam.points > gameOpponent.points) {
			record.wins++;
		} else if (gameTeam.points < gameOpponent.points) {
			record.losses++;
		} else {
			record.draws++;
		}
	}

	return (
		<>
			<ContentContainer>
				<div className="TeamHeader">
					<div className="TeamInfoContainer">
						<Logo team={team} />
						<div className="TeamInfo">
							<div className="NameContainer">
								<h1>{team.name}</h1>
								<div className="Record desktop-only">
									{record?.wins ?? 0} - {record?.losses ?? 0}
									{record?.draws > 0 ? <> - {record?.draws}</> : ''}
								</div>
							</div>
							{/* {team.champion_ot7_events?.length > 0 ? (
								<div className="Champions">
									{team.champion_ot7_events?.map((e) => (
										<a href={`/standings/${e.ot7_season_id}/tournament`} title={e.title}>
											<FontAwesomeIcon icon={faTrophy} color={styles.COLOR.BRAND_PRIMARY} />
										</a>
									))}
								</div>
							) : null} */}

							<div className="DivisionContainer">
								<div className="DivisionLogo">
									<img src={`${Urls.OvertimeImagesBaseUrl}/${division.logo_path}`} />
								</div>
								<div className="DivisionName">{division.name}</div>
								{division_won && <FontAwesomeIcon icon={faTrophy} color={styles.COLOR.BRAND_PRIMARY} />}
							</div>
							{team.city ? (
								<div className="Location">
									<FontAwesomeIcon icon={faLocationDot} />
									{team.city}, {team.state}
								</div>
							) : null}
							{isInvitedToChampionship ? (
								<div className="Invite">
									<FontAwesomeIcon icon={faShieldCheck} />
									Invited to OT7 Championship
								</div>
							) : null}
						</div>
					</div>
					<div className="Record mobile-only">
						{record?.wins ?? 0} - {record?.losses ?? 0}
						{record?.draws > 0 ? <> - {record?.draws}</> : ''}
					</div>
					<div className="DescriptionContainer">
						{team.description ? <div className="Description">{team.description}</div> : null}
						<TeamsLinks team={team} size={'lg'} />
					</div>
				</div>
			</ContentContainer>

			<ContentContainer>
				<FeaturedPlayers title="Players to Watch" players={players} maxPlayers={4} />
			</ContentContainer>

			<ContentContainer>
				<div className="RosterSchedule">
					<div className="Roster">
						<h2>Roster</h2>
						<Table
							headers={playerHeaders.map((h) => ({ value: h[0], label: h[1] }))}
							currentHeader={currentHeader}
							onHeaderSelect={setCurrentHeader}
							firstColumnHeader="Player"
							rows={[
								...sortedPlayers.map((row, i) => (
									<tr key={row.id ?? i}>
										<th className="PlayerName">{row.full_name}</th>
										{playerHeaders.map((h) => (
											<td key={h[0]} className="Statistic">
												{(h as any)[0] === 'pno' && !row[h[0]]
													? ''
													: ((h: string, v: number) =>
															h === 'height'
																? (v ?? 0) === 0
																	? '-'
																	: `${Math.floor(v / 12)}' ${v % 12}"`
																: ['position', 'weight', 'hometown'].includes(h)
																	? v || '-'
																	: v ?? 0)(h[0], get(row, h[0]))}
											</td>
										))}
									</tr>
								)),
							]}
						/>
						{sortedPlayers.length === 0 ? <TBD /> : null}
					</div>
					<Games team_id={team.id} showAll={true} />
				</div>
			</ContentContainer>
		</>
	);
};

const Team = () => {
	return (
		<Layout>
			<div className="Team">
				<Suspended>
					<_Team />
				</Suspended>
			</div>
		</Layout>
	);
};
export default Team;
